import React from "react";
import {
  MenuItem,
  Grid,
  Select,
  SelectChangeEvent,
  InputLabel,
  FormControl,
  Box
} from "@mui/material";

interface TimeDropdownProps {
  value?: string; // "HH:mm" 24-hour format, can be empty string
  onChange?: (time: string) => void;
}

const hours = [
  "",
  ...Array.from({ length: 12 }, (_, i) => (i + 1).toString().padStart(2, "0")),
];
const minutes = ["", "00", "15", "30", "45"];
const meridiems = ["", "AM", "PM"];

function parseTime(time?: string) {
  if (!time) {
    return { hour: "", minute: "", meridiem: "" };
  }
  const [rawHour, rawMinute] = time.split(":");
  if (!rawHour || !rawMinute) {
    return { hour: "", minute: "", meridiem: "" };
  }
  let h = parseInt(rawHour, 10);
  const m = rawMinute;

  if (isNaN(h) || !minutes.includes(m)) {
    return { hour: "", minute: "", meridiem: "" };
  }

  const mer = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;

  return {
    hour: h.toString().padStart(2, "0"),
    minute: m,
    meridiem: mer,
  };
}

const TimeDropdown: React.FC<TimeDropdownProps> = ({ value, onChange }) => {
  const [{ hour, minute, meridiem }, setTimeParts] = React.useState(() =>
    parseTime(value)
  );

  React.useEffect(() => {
    setTimeParts(parseTime(value));
  }, [value]);

  const updateTime = (
    newHour: string,
    newMinute: string,
    newMeridiem: string
  ) => {
    if (!newHour || !newMinute || !newMeridiem) {
      onChange?.("");
      return;
    }

    let h = parseInt(newHour, 10);
    if (newMeridiem === "PM" && h !== 12) {
      h += 12;
    }
    if (newMeridiem === "AM" && h === 12) {
      h = 0;
    }
    const formattedTime = `${h.toString().padStart(2, "0")}:${newMinute}`;
    onChange?.(formattedTime);
  };

  const handleChange =
    (type: "hour" | "minute" | "meridiem") => (e: SelectChangeEvent) => {
      const val = e.target.value;
      let newHour = hour;
      let newMinute = minute;
      let newMeridiem = meridiem;

      if (type === "hour") newHour = val;
      if (type === "minute") newMinute = val;
      if (type === "meridiem") newMeridiem = val;

      setTimeParts({ hour: newHour, minute: newMinute, meridiem: newMeridiem });
      updateTime(newHour, newMinute, newMeridiem);
    };

  return (
    <Box display="flex" gap={1} alignItems="center">
      <FormControl size={"small"} sx={{ width: 90 }}>
        <InputLabel>Hour</InputLabel>
        <Select
          fullWidth
          size="small"
          value={hour}
          onChange={handleChange("hour")}
          label="Hour"
        >
          {hours.map((hr) => (
            <MenuItem key={hr} value={hr}>
              {hr === "" ? "--" : hr}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size={"small"} sx={{ width: 90 }}>
        <InputLabel>Min</InputLabel>
        <Select
          fullWidth
          size="small"
          value={minute}
          label="Min"
          onChange={handleChange("minute")}
        >
          {minutes.map((min) => (
            <MenuItem key={min} value={min}>
              {min === "" ? "--" : min}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size={"small"} sx={{ width: 100 }}>
        <InputLabel>AM/PM</InputLabel>
        <Select
          fullWidth
          size="small"
          value={meridiem}
          label="AM/PM"
          onChange={handleChange("meridiem")}
        >
          {meridiems.map((m) => (
            <MenuItem key={m} value={m}>
              {m === "" ? "--" : m}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

export default TimeDropdown;
